"use client";

import React, { useEffect, useRef, useState, ReactNode } from 'react'

import { useSelectState } from 'react-stately';
import { HiddenSelect, useSelect } from 'react-aria';
import { Key } from '@react-types/shared';

import ListBox, { ListBoxStateless } from '../ListBox';
import { Pressable } from '../Button';
import { Popover } from '../PopoverTrigger';
import { ChevronDownIcon, ChevronUpIcon, Cross1Icon } from '@radix-ui/react-icons';

import clsx from 'clsx';

import styleClasses from './SelectInput.module.css';

import useResizeObserver from 'bwax-ui/hooks/useResizeObserver'
import ClampedText from '../ClampedText';

interface SelectItem {
  label: ReactNode;
  shortLabel?: string;
  value: string | number;
  isDisabled?: boolean;
  items?: SelectItem[];
}

interface SelectInputProps {
  items: SelectItem[];
  isMulti?: boolean;
  selected?: string | string[];
  onSelect: (selection: string | string[]) => void;
  placeholder?: string;
  listBoxStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  valueStyle?: React.CSSProperties;
  color?: string;
  label?: string;
  className?: string;
  listBoxClassName?: string;
  itemUnstyled?: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
  onClear?: () => void;
  name?: string;
  size?: 'small' | 'normal' | 'large';
  fitWidth?: boolean;
  [key: string]: any;
}


function renderLabel(label: ReactNode) {
    return typeof label == "string" ? (
        <ClampedText text={label} tipEnabled={false} />
    ) : label
}

// item: { label, value, isDisabled }
export default function SelectInput(props: SelectInputProps) {
    const {
        items, isMulti, ...otherProps
    } = props;

    function collectDisabledKeys(items: SelectItem[]): (string | number)[] {
        return items.flatMap(item => {
            const { label, value, isDisabled, items } = item
            return items ? collectDisabledKeys(items) : (isDisabled ? [value] : [])
        })
    }
    const disabledKeys = collectDisabledKeys(items);

    function renderItem(item: SelectItem, index: number) {
        const { label, value, items, } = item;

        if (items) {
            return (
                <ListBox.Section title={item.label} key={index} aria-label={String(item.label)}>
                    {items.map(renderItem)}
                </ListBox.Section>
            )
        } else {
            return (
                <ListBox.Item key={value} textValue={String(value)}>{
                    renderLabel(label)
                }</ListBox.Item>
            )
        }
    }

    return (
        <Select_comp {...otherProps} disabledKeys={disabledKeys} items={items} isMulti={isMulti}>
            {items.map(renderItem)}
        </Select_comp>
    )
}

interface Select_compProps extends Omit<SelectInputProps, 'items'> {
  items: SelectItem[];
  disabledKeys: (string | number)[];
  children: React.ReactNode;
}

function Select_comp(props: Select_compProps) {
    const {
        placeholder = "请选择", 
        listBoxStyle, 
        style, 
        valueStyle, 
        color, 
        label = "select",
        selected, 
        onSelect,
        isMulti,
        className, 
        listBoxClassName,
        itemUnstyled,
        isDisabled,
        isClearable, 
        onClear,
        items,
        size = 'normal',
        fitWidth = false,
        ...otherProps
    } = props;

    // 创建选择状态
    const state = useSelectState({
        ...otherProps,
        selectedKey: isMulti ? undefined : selected,
        onSelectionChange: (selection: Key | null) => {
            if(isMulti) {
                if(selection) {
                    const existing = selected as string[] || [];
                    // selection 总一个只有一个值，看看 selected 是否包含这个值, 如果包含，则删除，否则添加
                    const newValues = existing.indexOf(selection as string) !== -1 ? existing.filter(item => item !== selection) : [...existing, selection as string]; 
                    onSelect(newValues);                   
                }

            } else {
                onSelect(selection as string);
            }

        },
        // @ts-ignore - allowsEmptySelection 可能在某些版本中不存在
        allowsEmptySelection: true
    });


    useEffect(() => {
        state.selectionManager.setSelectionBehavior(isMulti ? 'replace' : 'toggle');
        // @ts-ignore - allowsMultipleSelection 属性在某些版本的类型中可能不存在
        state.selectionManager.allowsMultipleSelection = isMulti;

        // 避免不必要的状态更新
        if (isMulti) {
            // const newKeys = (selected || []).sort();
            // const currentKeys = Array.from(state.selectionManager.selectedKeys).sort();
            // if(JSON.stringify(newKeys) !== JSON.stringify(currentKeys)) {
            //     state.selectionManager.setSelectedKeys(new Set(newKeys));
            // }

        } else {
            // 单选模式下，只有当选中值确实改变时才更新
            if (selected !== state.selectedKey) {
                state.setSelectedKey(selected as Key);
            }
        }
    }, [selected, isMulti, state]);


    // if(isMulti) {
    //     console.log(">>> state", selected, state.selectedKey, state.selectionManager.selectedKeys)
    // }


    const ref = useRef<HTMLDivElement>(null);
    const {
        labelProps,
        triggerProps,
        valueProps,
        menuProps
    } = useSelect({
        label, ...otherProps, isDisabled
    }, state, ref);

    const [width, setWidth] = useState<number | undefined>();
    useResizeObserver(ref.current, entry => {
        if (entry && entry.borderBoxSize && entry.borderBoxSize[0]) {
            const width = entry.borderBoxSize[0].inlineSize;
            setWidth(width);
        }
    });

    function findAllLeafItems(items: SelectItem[]): SelectItem[] {
        return items.flatMap(item => {
            const { items } = item;
            return items ? findAllLeafItems(items) : [item];
        });
    }

    const allLeafItems = findAllLeafItems(items);

    // 获取选中的项目
    const selectedItems = isMulti 
        ? ((selected as string[]) || []).map(key => allLeafItems.find(item => item.value === key))
        : selected ? [allLeafItems.find(item => item.value === selected)] : [];


    return (
        <>
            <HiddenSelect
                isDisabled={props.isDisabled}
                state={state}
                triggerRef={ref}
                label={props.label}
                name={props.name}
            />
            <Pressable
                {...triggerProps}
                ref={ref}
            >
                <div data-color={"main"} className={clsx(
                    styleClasses["lc-select-input"], 
                    "border bg-[--lc-input-bg] rounded flex items-center justify-between",
                    (state.isOpen ? "border-[--color-9]" : "border-transparent"),
                    className, 
                    isClearable && (isMulti ? selectedItems.length > 0 : state.selectedKey) ? styleClasses["clearable"] : null,                    
                    { 
                        "opacity-60 cursor-not-allowed" : isDisabled,
                        "hover:bg-[--lc-input-bg-hover]": !(isMulti && ((selected as string[]) || []).length > 0) && !isDisabled,
                    },
                    {
                        "py-0.5 pl-1 pr-2 font-size-14": size === 'normal',
                        "py-0 pl-0 pr-1 font-size-12": size === 'small',
                        "py-1 pl-1.5 pr-3 font-size-16": size === 'large',
                    }
                )} style={{ ...style }}>
                    <div {...valueProps} style={valueStyle} className={clsx(
                        "flex flex-wrap gap-1",
                        {
                            "py-0.5 pl-2 pr-1": size === 'small',
                            "py-1 pl-2 pr-1": size === 'normal',
                            "py-1.5 px-3 pr-1.5": size === 'large',
                        }
                    )}>
                        {selectedItems.length > 0
                            ? (isMulti ? (
                                selectedItems.map((item, index) => (
                                    <div key={index} className={clsx("bg-[--color-3] px-1.5 py-0.5 rounded flex items-center gap-1")}>
                                        {renderLabel(item?.shortLabel || item?.label)}
                                        <Pressable onPress={_ => {
                                            const existing = (selected as string[]) || [];
                                            const newValues = existing.filter(i => i !== item?.value)
                                            onSelect(newValues);
                                        }}>
                                            <i className='bx bx-x cursor-pointer'></i>
                                        </Pressable>
                                    </div>
                                ))
                            ) : renderLabel(selectedItems[0]?.shortLabel || selectedItems[0]?.label))
                            : (placeholder ? (
                                <ClampedText className={"text-[--gray-10]"}>
                                    {placeholder}
                                </ClampedText>
                            ) : null)}
                    </div>
                    <div aria-hidden="true" className={clsx(styleClasses["arrow-icon"], styleClasses["icon"])}>
                        {state.isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    </div>
                    {isClearable && selectedItems.length > 0 && (
                        <Pressable onPress={_ => {
                            if(onClear) {
                                onClear()
                            }
                            if (isMulti) {
                                state.selectionManager.setSelectedKeys(new Set());
                            } else {
                                state.setSelectedKey(null);
                            }
                        }}>
                            <div className={clsx(styleClasses["clear-icon"], styleClasses["icon"], "rounded-md")}>
                                <i className='bx bxs-x-circle'></i>
                            </div>
                        </Pressable>
                    )}
                </div>
            </Pressable>
            {state.isOpen && (
                <Popover state={state} triggerRef={ref} placement="bottom start" className={styleClasses["lc-select-popover"]} withArrow={false} offset={4}>
                    <ListBoxStateless
                        {...menuProps}
                        state={state}
                        selected={selected}
                        color={color}
                        className={listBoxClassName}
                        style={{
                            ...listBoxStyle,
                            ...(fitWidth ? { width } : { minWidth: width })
                        }}
                        isMulti={isMulti}
                        itemUnstyled={itemUnstyled}
                        size={size}
                    />
                </Popover>
            )}
        </>
    )
}